import axios from "axios";
import { useEffect, useState } from "react";
import { Button, Card, CardContent, Link, Typography } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import localize from "../../common/localize";
import NoDataPage from "../../components/noDataPage/NoDataPage";
import ModalBackground from "../../components/common/ModalBackground";
import PaymentReport from "../event/components/PaymentReport";
import {
  getClearEventPaymentModel,
  getCurrencyFromSetup,
  getEventTotalValue,
} from "../../common/EntitiesManagement";
import TableComponent from "../../components/common/TableComponent";
import TrueFalseImage from "../../components/TrueFalseImage";
import {
  GetDateInDDMMYYYYFormat,
  GetDateInYYYYMMDDWithoutTimeFormat,
  generateNoDataValues,
  getOperationalStatusNameFromId,
  getOperationalStatusStyleName,
  getValueInCurrency,
} from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import ActionButtons from "../../components/common/ActionButtons";
import EventSearch from "../../components/common/EventSearch";
import { BackForeColor } from "../../components/common/BackForeColor";
import TotalValueItem from "../event/components/TotalValueItem";

export default function Payments() {
  const { config, EVENTS_API } = useGlobalContext();
  const [eventsList, setEventsList] = useState([]);
  const [searchList, setSearchList] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showEventPayment, setShowEventPayment] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [paymentPreviousData, setPaymentPreviousData] = useState(null);

  useEffect(() => {
    if (searchList?.length > 0) {
      setEventsList([...eventsList, ...searchList]);
    }
  }, [searchList]);

  const AddPaymentRegister = () => {
    setPaymentInfo(getClearEventPaymentModel());
    setPaymentPreviousData(getClearEventPaymentModel());
    setShowEventPayment(true);
  };

  const UpdatePaymentDate = async () => {
    try {
      if (eventsList?.length > 0) {
        const result = await axios.put(
          `${EVENTS_API}/payment`,
          {
            ...paymentInfo,
            PaymentDate: GetDateInYYYYMMDDWithoutTimeFormat(
              paymentInfo.PaymentDate
            ),
            EventIds: eventsList.map((x) => x.Id),
          },
          config
        );

        validateOperationResult(result.data);
      }
    } catch (error) {
      setSuccessMessage(`${localize.error_unexpected}: ${error.message}`);
      setShowSuccessMessage(true);
    }
  };

  const validateOperationResult = (result) => {
    try {
      if (!result.Ok) {
        const data = JSON.parse(result?.data || result?.Data);
        const codes = [];
        const newEventsList = [];

        for (let index = 0; index < data.length; index += 1) {
          const eventIndex = eventsList.findIndex((x) => x.Id === data[index]);
          if (eventIndex >= 0) {
            newEventsList.push(eventsList[eventIndex]);
            codes.push(eventsList[eventIndex].Code);
          }
        }

        setEventsList([...newEventsList]);

        setSuccessMessage(
          `${localize.error_saving_payment_info}: ${JSON.stringify(codes)}`
        );
        setShowSuccessMessage(true);
        return;
      }

      setPaymentInfo(null);
      setPaymentPreviousData(null);
      setEventsList([]);
      setSuccessMessage(localize.success_payment_registration_message);
      setShowSuccessMessage(true);
    } catch (error) {
      setSuccessMessage(`${localize.error_unexpected}: ${error.message}`);
      setShowSuccessMessage(true);
    }
  };

  const RemoveEventFromList = (eventId) => {
    const index = eventsList.findIndex((x) => x.Id === eventId);
    if (index >= 0) {
      eventsList.splice(index, 1);
      setEventsList([...eventsList]);
    }
  };

  const paymentInfoTableTitles = () => {
    const titles = [localize.payed];

    if (paymentInfo.Payed) {
      titles.push(localize.payment_date);
      titles.push(localize.payment_method);
    } else {
      titles.push(localize.motivation);
    }

    return titles;
  };

  const paymentInfoData = () => {
    const data = [];

    data.push({
      key: "payment_register_key",
      data: [
        <ActionButtons
          onclickForDeleteButton={() => {
            setShowDeleteConfirmation(true);
          }}
          hideEditButton
        />,
        <TrueFalseImage value={paymentInfo.Payed} />,
        paymentInfo.Payed ? (
          GetDateInDDMMYYYYFormat(new Date(paymentInfo.PaymentDate))
        ) : (
          <ValueOrNoDataLabel data={paymentInfo.NoPaymentObservation} />
        ),
      ],
    });

    if (paymentInfo.Payed) {
      data[0].data.push(
        <ValueOrNoDataLabel data={paymentInfo.PaymentMethod?.Name} />
      );
    }

    return data;
  };

  const mustShowPaymentData = () => {
    if (!paymentInfo) {
      return false;
    }

    return (
      paymentInfo.PaymentMethodId > 0 ||
      paymentInfo.NoPaymentObservation?.length > 0
    );
  };

  const getTotalValue = () => {
    if (eventsList?.length > 0) {
      let sum = 0;
      eventsList.map(
        (x) =>
          (sum += getEventTotalValue(x, true) + (x.AdditionalCostValue ?? 0))
      );
      return getValueInCurrency(getCurrencyFromSetup(), sum);
    }

    return getValueInCurrency(getCurrencyFromSetup(), 0);
  };

  return (
    <div className="lateral_padding">
      {showSuccessMessage && (
        <ModalBackground
          showModal={showSuccessMessage}
          setShowModal={setShowSuccessMessage}
          title={localize.payment_info}
          okButtonText={localize.accept}
          hideCancelButton
          onClickToSubmit={() => {
            setShowSuccessMessage(false);
          }}
          Component={<Typography>{successMessage}</Typography>}
        />
      )}

      {showSearch && (
        <EventSearch
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          setResultList={setSearchList}
        />
      )}

      {showSaveConfirmation && (
        <ModalBackground
          showModal={showSaveConfirmation}
          setShowModal={setShowSaveConfirmation}
          title={localize.finish_register_payment}
          okButtonText={localize.continue}
          onClickToCancel={() => {
            setShowSaveConfirmation(false);
          }}
          onClickToSubmit={() => {
            UpdatePaymentDate();
            setShowSaveConfirmation(false);
          }}
          Component={
            <Typography>{localize.save_payment_confirmation}</Typography>
          }
        />
      )}

      {showDeleteConfirmation && (
        <ModalBackground
          showModal={showDeleteConfirmation}
          setShowModal={setShowDeleteConfirmation}
          title={localize.delete_payment_info}
          okButtonText={localize.delete}
          onClickToCancel={() => {
            setShowDeleteConfirmation(false);
          }}
          onClickToSubmit={() => {
            setPaymentInfo(null);
            setShowDeleteConfirmation(false);
          }}
          Component={
            <Typography>{localize.delete_payment_info_confirmation}</Typography>
          }
        />
      )}

      {showEventPayment && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEventPayment}
          setShowModal={setShowEventPayment}
          title={localize.register_payment}
          okButtonText={localize.continue}
          onClickToCancel={() => {
            setPaymentPreviousData(getClearEventPaymentModel());
            setPaymentInfo(getClearEventPaymentModel());
            setShowEventPayment(false);
          }}
          onClickToSubmit={() => {
            setPaymentInfo({
              ...paymentPreviousData,
            });
            setPaymentPreviousData(getClearEventPaymentModel());
            setShowEventPayment(false);
          }}
          validators={
            (paymentPreviousData?.Payed &&
              (!paymentPreviousData?.PaymentMethodId ||
                paymentPreviousData?.PaymentMethodId <= 0)) ||
            (!paymentPreviousData?.Payed &&
              (!paymentPreviousData?.NoPaymentObservation ||
                paymentPreviousData?.NoPaymentObservation?.length <= 0))
          }
          Component={
            <PaymentReport
              paymentModel={paymentPreviousData}
              setPaymentModel={setPaymentPreviousData}
            />
          }
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.register_payment}
            subtitle={localize.register_payment_description}
          />
        </CardContent>
      </Card>

      {!mustShowPaymentData() ? (
        <NoDataPage
          showAddButton
          pageTitle={localize.no_payment_info}
          onAddButtonClick={() => {
            AddPaymentRegister();
          }}
        />
      ) : (
        <div className="flex flex-col">
          <TableComponent
            firstColumnTitle={localize.actions}
            columnTitles={paymentInfoTableTitles()}
            results={paymentInfoData()}
          />

          <div
            className="flex justify-between items-bottom"
            style={{ marginRight: "18px" }}
          >
            <div>
              <PageTitleSubtitle
                title={localize.service_orders}
                subtitle={localize.add_events_to_payment_description}
              />
            </div>

            <div className="flex" style={{ flexDirection: "column-reverse" }}>
              <p className="lateral_padding" style={{ alignSelf: "end" }}>{`${
                localize.total_value
              }: ${getTotalValue()}`}</p>

              <div
                className="flex"
                style={{
                  gap: "10px",
                  alignItems: "end",
                }}
              >
                <Button
                  sx={{ backgroundColor: "#10366E", height: "30px" }}
                  variant="contained"
                  color="info"
                  onClick={() => {
                    setSearchList([]);
                    setShowSearch(true);
                  }}
                >
                  {localize.select}
                </Button>

                <Button
                  sx={{ height: "30px" }}
                  variant="contained"
                  color="success"
                  disabled={!eventsList || eventsList.length === 0}
                  onClick={() => {
                    setShowSaveConfirmation(true);
                  }}
                >
                  {localize.save_changes}
                </Button>
              </div>
            </div>
          </div>

          <TableComponent
            firstColumnTitle={localize.actions}
            columnTitles={[
              localize.code,
              localize.event_type,
              localize.operational_status,
              localize.execution_status,
              localize.proceeding,
              localize.client_name,
              localize.total_value,
            ]}
            results={
              eventsList && eventsList.length > 0
                ? eventsList.map((item) => ({
                    key: item.Id,
                    data: [
                      <ActionButtons
                        hideEditButton
                        key={`${item.Id}_action`}
                        onclickForDeleteButton={() => {
                          RemoveEventFromList(item.Id);
                        }}
                      />,
                      <Link
                        color="primary"
                        underline="always"
                        href="#"
                        onClick={() => {
                          window.open(`/events/detail/${item.Id}`, "_blank");
                        }}
                      >{`${item.Code}`}</Link>,
                      <BackForeColor
                        backColor={item.EventType.BackgroundColor}
                        foreColor={item.EventType.ForegroundColor}
                        text={item.EventType.Name}
                      />,
                      <p
                        style={{ borderRadius: "40px" }}
                        className={`rounded p-2 ${getOperationalStatusStyleName(
                          item.OperationalStatus
                        )}`}
                      >
                        {getOperationalStatusNameFromId(item.OperationalStatus)}
                      </p>,
                      <BackForeColor
                        backColor={
                          item.OperationalStatus === 0
                            ? "white"
                            : item.CurrentStatus?.BackgroundColor
                        }
                        foreColor={
                          item.OperationalStatus === 0
                            ? "red"
                            : item.CurrentStatus?.ForegroundColor
                        }
                        text={
                          item.OperationalStatus === 0
                            ? localize.cancelled
                            : item.CurrentStatus?.Name
                        }
                        styleName={
                          item.OperationalStatus === 0
                            ? "cancelled_operational_status"
                            : ""
                        }
                        TooltipText={
                          item.OperationalStatus === 0
                            ? item.FinishObservation
                            : undefined
                        }
                      />,
                      <ValueOrNoDataLabel data={item.ProceedingInfo?.Name} />,
                      <ValueOrNoDataLabel data={item.Client?.Name} />,
                      <TotalValueItem item={item} />,
                    ],
                  }))
                : [generateNoDataValues(8)]
            }
          />
        </div>
      )}
    </div>
  );
}
